import React from 'react';
import { useEffect, useState } from 'react';
import { MarsApiService as api } from '../../../api/mars-api-service';
import { MarsApiConfig as apiConfig } from '../../../api/mars-api-config';
import { IGridConfig } from '../../../types/grid';
import { useTranslation } from 'react-i18next';
import { Restricted, usePermission } from '../../../contexts/PermissionContext';
import Grid from '../../../components/common/grid/Grid';
import Switch from 'devextreme-react/switch';
import { useNavigate } from 'react-router-dom';
import icons from '../../../components/common/icons/icons';
import Button from 'devextreme-react/button';
import { useLoaderContext } from '../../../contexts/LoaderContext';
import ConfirmDialog from '../../../components/common/confirm/ConfirmDialog';
import { IDialogConfig } from '../../../types/dialog';

export default function ViewMyWatchList() {
  const [myWatchList, setMyWatchList] = useState();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIsLoading } = useLoaderContext();
  const [activeConfirmDialog, setActiveConfirmDialog] = useState(false);
  const { findFeature, featureList } = usePermission();

  const getMyWatchList = async () => {
    try {
      const data = await api.get({ url: apiConfig.myWatchList });
      setMyWatchList(data);
    } catch (err) {
      console.error('Error fetching myWatchList:', err);
    }
  };

  const confirmStatusDialogConfig: IDialogConfig = {
    id: 'confirmSave',
    handleSubmit: () => {
      setActiveConfirmDialog(false);
    },
    handleClose: () => {
      getMyWatchList();
      setActiveConfirmDialog(false);
    },
  };

  const [activeDialogConfig, setActiveDialogConfig] = useState(
    confirmStatusDialogConfig
  );

  useEffect(() => {
    getMyWatchList();
  }, []);

  const yesNoDataSource = [
    {
      name: t('labels.active'),
      value: true,
    },
    {
      name: t('labels.inActive'),
      value: false,
    },
  ];

  const onRowClick = (e: any) => {
    const clickedInsideActionCell = e.event.target.closest('.action-cell');

    if (
      clickedInsideActionCell ||
      [
        'dx-switch-on',
        'dx-switch-off',
        'dx-switch-handle',
        'dx-button-content',
      ].includes(e.event.target.className)
    ) {
      // Prevent navigation
      return;
    }

    navigate('/my-watch-list/edit', { state: { fleetId: e.key.fleetId } });
  };

  const gridConfig: IGridConfig = {
    testId: 'myWatchListViewGrid',
    dataSource: myWatchList,
    defaultColumns: findFeature('MyWatchList.ListFleet')
      ? [
          {
            caption: t('labels.fleetName'),
            dataField: 'fleetName',
            dataType: 'string',
            minWidth: 150,
          },
          {
            dataField: 'color',
            caption: 'Color',
            alignment: 'center',
            minWidth: 150,
            allowFiltering: false,
            allowSorting: false,
            cellTemplate: function (container: any, options: any) {
              const color = options.value;

              const box = document.createElement('div');
              box.style.width = '16px';
              box.style.height = '16px';
              box.style.backgroundColor = color;
              box.style.border = '1px solid #ccc';
              box.style.borderRadius = '4px';
              box.style.display = 'inline-block';

              box.style.marginLeft = '8px';
              container.appendChild(box);
            },
          },
          {
            caption: t('labels.active'),
            alignment: 'center',
            dataField: 'active',
            dataType: 'string',
            minWidth: 150,
            cellTemplate: 'statusTemplate',
            allowFiltering: true,
            allowSorting: true,
            lookup: {
              dataSource: yesNoDataSource,
              valueExpr: 'value',
              displayExpr: 'name',
            },
          },
          {
            caption: t('labels.action'),
            dataField: 'action',
            dataType: 'string',
            cellTemplate: 'actionTemplate',
            alignment: 'center',
            minWidth: 100,
            allowFiltering: false,
            allowSorting: false,
            visible: findFeature('MyWatchList.DeleteFleet'),
          },
        ]
      : [],
    showHeader: true,
    header: t('headers.mywatchlist'),
    showAddButton: findFeature('MyWatchList.AddFleet'),
    showExport: false,
    hidePagination: !findFeature('MyWatchList.ListFleet'),
    noDataText: t('labels.companyListNoDataText'),
    rowClick: findFeature('MyWatchList.EditFleet') ? onRowClick : undefined,
    onClickAddButton: () => {
      navigate('/my-watch-list/add');
    },
  };

  const cellRender = (data: any) => {
    return (
      <div className="m-l-field-wrap">
        <div className="m-l-toggle-switch">
          <Restricted permission="GlobalList.ActivateInactiveList">
            <Switch
              elementAttr={{ 'data-testid': 'statusToggleswitch' }}
              defaultValue={data.value}
              onValueChange={(e) => {
                handleActiveInactiveChange(e, data.data, false);
              }}
            />
          </Restricted>
        </div>
      </div>
    );
  };

  const actionCellTemplate = (data: any) => {
    return (
      <div className="action-cell">
        <Button
          className="m-l-btn--icon-plain"
          onClick={(e) => {
            handleActiveInactiveChange(e, data.data, true);
          }}
        >
          <icons.Trash />
        </Button>
      </div>
    );
  };

  const handleActiveInactiveChange = async (
    e: any,
    fleet: any,
    isDelete: boolean
  ) => {
    if (isDelete) {
      const dialogConfig = {
        title: t('toast.confirmation'),
        content: t('toast.deleteFleet'),
        handleSubmit: () => {
          deleteFleet(fleet?.fleetId);
          setActiveConfirmDialog(false);
        },
      };
      setActiveDialogConfig({
        ...confirmStatusDialogConfig,
        ...dialogConfig,
      });
      setActiveConfirmDialog(true);
    } else {
      const dialogConfig = {
        title: t('toast.confirmation'),
        content: e
          ? t('toast.activeFleetStatus')
          : t('toast.deactiveFleetStatus'),
        handleSubmit: () => {
          activateList(e, fleet?.fleetId);
          setActiveConfirmDialog(false);
        },
      };
      setActiveDialogConfig({
        ...confirmStatusDialogConfig,
        ...dialogConfig,
      });
      setActiveConfirmDialog(true);
    }
  };

  const activateList = async (isactive: boolean, fleetId: any) => {
    const statusJson: any = {
      fleetId: fleetId,
      active: isactive,
    };
    api
      .put({ url: apiConfig.fleetStatus, data: statusJson }, setIsLoading)
      .then(() => {
        getMyWatchList();
      });
  };

  const deleteFleet = async (fleetId: any) => {
    const statusJson: any = {
      fleetId: fleetId,
    };
    api
      .delete({ url: apiConfig.fleetDelete, data: statusJson }, setIsLoading)
      .then(() => {
        getMyWatchList();
      });
  };

  return (
    <div className="inner-wrapper page-inner-wrap">
      <ul aria-label="breadcrumb" className="m-c-bredcrumb">
        <li>
          <a className="active">{t('headers.mywatchlist')}</a>
        </li>
      </ul>

      <Grid
        gridConfig={gridConfig}
        statusTemplate={cellRender}
        actionTemplate={actionCellTemplate}
      />

      <ConfirmDialog
        dialogConfig={activeDialogConfig}
        isOpen={activeConfirmDialog}
      />
    </div>
  );
}
